/* global React, Icon, Card, Button, Modal, Pagination, Select, toast, fmt, usePaged */
// ============================================================
// PLUMME 3 — Privat økonomi · Kontoer (Import)
// Registrer og sorter kontoene dine etter type. Kontoene og de
// opplastede kontoutskriftene kommer fra backend (readmodel fra
// getPersonalFinanceOverview), ikke fra lokal state.
// ============================================================
const { useState: koUseState, useMemo: koUseMemo } = React;

// Kontotypene backend kjenner. Ikon og farge følger designet.
const KO_TYPES = {
  bank: { get label() { return t('po.currentAccount'); }, icon: 'wallet', color: 'var(--plum-600)' },
  savings: { get label() { return t('pay.savingsAccount'); }, icon: 'bank', color: 'var(--success)' },
  'credit-card': { get label() { return t('po.creditCard'); }, icon: 'card', color: 'var(--warning)' },
};
const KO_TYPE_LIST = Object.keys(KO_TYPES);
const koType = (kind) => KO_TYPES[kind] || KO_TYPES.bank;
const koLast4 = (accountNumber) => String(accountNumber || '').replace(/\D/g, '').slice(-4);
const koBalance = (account) => {
  const value = account?.currentBalance ?? account?.closingBalance;
  return value === null || value === undefined ? null : Number(value) || 0;
};

// Delt bekreftelsesdialog for irreversible sletting i privatøkonomi-importen.
function KoForm({ account, onClose, onDelete, onSave, title }) {
  const [draft, setDraft] = koUseState(() => account || { accountNumber: '', closingBalance: '', kind: 'bank', name: '', openingBalance: '' });
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const field = (label, key, opts = {}) => <label className="gj-field">
    <span>{label}</span>
    <input className="gj-input" inputMode={opts.inputMode} value={draft[key] ?? ''} placeholder={opts.placeholder || ''} onChange={(event) => set(key, event.target.value)} />
  </label>;
  const save = () => {
    if (!String(draft.name || '').trim()) {
      toast(t('pkn.acc.fillName'), 'error');
      return;
    }
    onSave?.({
      ...draft,
      accountNumber: draft.accountNumber,
      closingBalance: draft.closingBalance,
      currentBalance: draft.closingBalance,
      id: draft.id,
      kind: draft.kind || 'bank',
      name: String(draft.name || '').trim(),
      openingBalance: draft.openingBalance,
    });
  };
  return <Modal open onClose={onClose} title={title || (account ? t('pkn.acc.editAccount') : t('pkn.acc.addAccount'))} max={560}
    footer={<React.Fragment>
      {account && onDelete && <Button variant="ghost" icon="trash" onClick={() => onDelete(account)}>{t('list.delete')}</Button>}
      <div style={{ flex: 1 }} />
      <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="check" onClick={save} data-help={t('helpTopics.privateImport.body')} data-help-title={t('helpTopics.privateImport.title')}>{t('po.saveAccount')}</Button>
    </React.Fragment>}>
    <div className="gj-form">
      {field(t('pkn.acc.fieldName'), 'name', { placeholder: t('pkn.acc.phName') })}
      {field(t('pkn.acc.fieldNumber'), 'accountNumber', { placeholder: t('pkn.acc.phNumber') })}
      <label className="gj-field"><span>{t('sale.pay.accountType')}</span>
        <Select value={draft.kind || 'bank'} onChange={(event) => set('kind', event.target.value)}>
          {KO_TYPE_LIST.map((kind) => <option key={kind} value={kind}>{KO_TYPES[kind].label}</option>)}
        </Select>
      </label>
      <div className="gj-form-row">
        {field(t('pkn.acc.openingBalance'), 'openingBalance', { inputMode: 'decimal', placeholder: t('pkn.gj.optionalUpper') })}
        {field(t('pkn.acc.closingBalance'), 'closingBalance', { inputMode: 'decimal', placeholder: t('pkn.gj.optionalUpper') })}
      </div>
      <span className="gj-hint">{t('pkn.acc.numberHint')}</span>
    </div>
  </Modal>;
}

function KoCard({ account, statements, transactions, onOpen, onEdit }) {
  const type = koType(account.kind);
  const balance = koBalance(account);
  const negative = balance !== null && balance < 0;
  return <div className="gj-card" role="button" tabIndex={0} onClick={onOpen}
    onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(); } }}>
    <div className="gj-card-top">
      <span className="gj-card-ico" style={{ background: type.color }}><Icon name={type.icon} size={19} /></span>
      <div className="gj-card-head">
        <div className="gj-card-name">{account.name || t('pkn.acc.account')}</div>
        <div className="gj-card-type">{account.accountNumber ? `•••• ${koLast4(account.accountNumber) || account.accountNumber}` : t('pkn.acc.noNumber')}</div>
      </div>
      <button className="aii-cost-more" onClick={(event) => { event.stopPropagation(); onEdit(); }} title={t('pkn.gj.editNamed', { name: account.name || t('pkn.acc.accountLower') })} aria-label={t('pkn.gj.editNamed', { name: account.name || t('pkn.acc.accountLower') })}><Icon name="pencil" size={15} /></button>
    </div>
    <div className="gj-card-bal" style={{ color: negative ? 'var(--error)' : 'var(--ink)' }}>{balance === null ? t('pkn.acc.noBalance') : fmt(balance)}</div>
    <div className="gj-card-foot" style={{ marginTop: 12 }}>
      <span className="gj-chip" style={{ background: type.color }}>{type.label}</span>
      <span className="gj-card-pmt">{transactions ? t('pkn.acc.transactionsCount', { count: transactions }) : t(statements === 1 ? 'pkn.acc.cardStatementOne' : 'pkn.acc.cardStatementMany', { count: statements })}</span>
    </div>
  </div>;
}

const koJobId = (transaction) => transaction?.importJobId || transaction?.jobId || '';
const koJobPeriod = (job) => {
  const start = String(job?.statementPeriodStart || '');
  const end = String(job?.statementPeriodEnd || '');
  if (!start && !end) return '';
  return start && end && start !== end ? `${start}–${end}` : start || end;
};
const koJobMatchesYear = (job, year, transactions) => {
  const target = String(year || '');
  const explicitYears = Array.isArray(job?.statementYears) ? job.statementYears.map(String) : [];
  if (explicitYears.length) return explicitYears.includes(target);
  if (String(job?.statementPeriodStart || '').startsWith(target) || String(job?.statementPeriodEnd || '').startsWith(target)) return true;
  const hasTransaction = transactions.some((transaction) => koJobId(transaction) === job.id);
  if (hasTransaction) return true;
  if (Number(job?.counts?.transactions || 0) > 0 || job?.status === 'processed') return false;
  return String(job?.createdAt || '').startsWith(target);
};

function KoDetail({ account, deletingJobId, jobs, transactions, onBack, onDeleteJob, onEdit, year }) {
  const Sec = window.PoSecHead;
  const type = koType(account.kind);
  const balance = koBalance(account);
  const negative = balance !== null && balance < 0;
  const categories = new Set(transactions.map((transaction) => transaction.category).filter(Boolean));
  const sortedJobs = koUseMemo(() => jobs.slice().sort((a, b) => String(b.statementPeriodEnd || b.processedAt || b.createdAt || '').localeCompare(String(a.statementPeriodEnd || a.processedAt || a.createdAt || ''))), [jobs]);
  const jobsPage = usePaged(sortedJobs, 10);
  const kpis = [
    ['list', t('pkn.acc.kpi.read'), transactions.length],
    ['fileText', t('pkn.acc.kpi.statements'), jobs.length],
    ['receipt', t('pkn.acc.kpi.categories'), categories.size],
    ['sparkle', t('pkn.acc.kpi.unprocessed'), jobs.filter((job) => job.status !== 'processed').length],
  ];
  return <Card>
    <div className="row gap-12" style={{ alignItems: 'center', marginBottom: 4 }}>
      <Button variant="ghost" size="sm" icon="arrowLeft" onClick={onBack}>{t('po.allAccounts')}</Button>
    </div>
    <Sec icon={type.icon} title={account.name || t('pkn.acc.account')}
      sub={[account.accountNumber ? `•••• ${koLast4(account.accountNumber) || account.accountNumber}` : null, type.label].filter(Boolean).join(' · ')}
      right={<div className="row gap-10" style={{ alignItems: 'center' }}>
        <span className="gj-card-bal" style={{ fontSize: 20, margin: 0, color: negative ? 'var(--error)' : 'var(--ink)' }}>{balance === null ? t('pkn.acc.noBalance') : fmt(balance)}</span>
        <Button size="sm" variant="secondary" icon="pencil" onClick={onEdit}>{t('cust.act.edit')}</Button>
      </div>} />

    <div className="po-proc-grid" style={{ marginTop: 14 }}>
      {kpis.map(([icon, label, value]) => <div key={label} className="po-proc">
        <div className="po-proc-top"><Icon name={icon} size={15} />{label}</div>
        <div className="po-proc-val">{value}</div>
      </div>)}
    </div>

    <div style={{ marginTop: 22 }}>
      <Sec icon="upload" title={t('po.uploadedStatements')} sub={t('pkn.acc.statementsFor', { year, name: account.name || t('pkn.acc.theAccount') })} />
      {jobs.length === 0
        ? <div className="gj-empty">{t('pkn.acc.noStatementsForYear', { year })}</div>
        : <div className="po-upload-list">
          {jobsPage.slice.map((job) => {
            const file = job.file || {};
            const lines = transactions.filter((transaction) => koJobId(transaction) === job.id).length;
            return <div key={job.id} className="po-upload-item">
              <span className="po-upload-ico"><Icon name="fileText" size={18} /></span>
              <div className="po-upload-meta">
                <div className="po-upload-name">{file.name || t('pkn.acc.statement')}</div>
                <div className="po-upload-sub">{[String(file.mimeType || '').split('/').pop()?.toUpperCase(), koJobPeriod(job), lines ? t('pkn.acc.transactionsCount', { count: lines }) : null].filter(Boolean).join(' · ') || t('pkn.acc.uploaded')}</div>
              </div>
              {job.status === 'processed'
                ? <span className="pp pp-paid">✓ {t('pkn.acc.readIn')}</span>
                : <span className="pp pp-warn">{job.status === 'failed' ? t('pkn.acc.failed') : t('pkn.acc.reading')}</span>}
              {onDeleteJob && <button className="po-upload-delete" type="button" disabled={deletingJobId === job.id}
                onClick={() => onDeleteJob(job)} title={t('po.deleteStatement')} aria-label={t('po.deleteStatement')} data-help={t('helpTopics.privateImport.body')} data-help-title={t('helpTopics.privateImport.title')}>
                <Icon name="trash" size={16} />
              </button>}
            </div>;
          })}
        </div>}
      {jobs.length > 0 && <div className="mt-16"><Pagination {...jobsPage} onPage={jobsPage.setPage} pageSize={jobsPage.ps} onPageSize={(size) => { jobsPage.setPs(size); jobsPage.setPage(1); }} pageSizes={[10, 25, 50]} /></div>}
    </div>
  </Card>;
}

function PoKontoer({ accounts = [], jobs = [], transactions = [], onSaveAccount, onDeleteAccount, onDeleteJob, year }) {
  const Sec = window.PoSecHead;
  const [filter, setFilter] = koUseState('alle');
  const [formAccount, setFormAccount] = koUseState(null);
  const [detailId, setDetailId] = koUseState(null);
  const [confirmAccount, setConfirmAccount] = koUseState(null);
  const [confirmJob, setConfirmJob] = koUseState(null);
  const [deletingJobId, setDeletingJobId] = koUseState('');

  const jobsFor = (accountId) => jobs.filter((job) => job.accountId === accountId);
  const transactionsFor = (accountId) => transactions.filter((transaction) => transaction.accountId === accountId);
  const jobsForYear = (accountId) => {
    const accountTransactions = transactionsFor(accountId);
    return jobsFor(accountId).filter((job) => koJobMatchesYear(job, year, accountTransactions));
  };

  const typesPresent = koUseMemo(() => KO_TYPE_LIST.filter((kind) => accounts.some((account) => (account.kind || 'bank') === kind)), [accounts]);
  const shown = filter === 'alle' ? accounts : accounts.filter((account) => (account.kind || 'bank') === filter);

  const balances = accounts.map(koBalance).filter((value) => value !== null);
  const deposits = balances.filter((value) => value > 0).reduce((sum, value) => sum + value, 0);
  const credit = balances.filter((value) => value < 0).reduce((sum, value) => sum + value, 0);

  const detailAccount = detailId ? accounts.find((account) => account.id === detailId) : null;
  const form = formAccount
    ? <KoForm
      account={formAccount.id ? formAccount : null}
      onClose={() => setFormAccount(null)}
      onDelete={(account) => { setFormAccount(null); setConfirmAccount(account); }}
      onSave={(account) => { onSaveAccount?.(account); setFormAccount(null); }}
    />
    : null;

  const deleteJob = async (job) => {
    if (!job?.id || deletingJobId) return;
    setDeletingJobId(job.id);
    try { await onDeleteJob?.(job); } finally { setDeletingJobId(''); }
  };

  const confirmStatements = confirmAccount ? jobsFor(confirmAccount.id).length : 0;
  const confirmTransactions = confirmAccount ? transactionsFor(confirmAccount.id).length : 0;
  const dialogs = <React.Fragment>
    {form}
    {confirmAccount && <ConfirmModal
      title={t('pkn.acc.deleteTitle', { name: confirmAccount.name || t('pkn.acc.theAccount') })}
      confirmLabel={t('pkn.acc.deleteAccount')}
      cancelLabel={t('common.cancel')}
      onClose={() => setConfirmAccount(null)}
      onConfirm={() => {
        const account = confirmAccount;
        setConfirmAccount(null);
        setDetailId(null);
        onDeleteAccount?.(account);
      }}
    >
      {confirmStatements > 0
        ? <React.Fragment>{t('pkn.acc.deleteWith')} <b>{t(confirmStatements === 1 ? 'pkn.acc.statementOne' : 'pkn.acc.statementMany', { count: confirmStatements })}</b>{confirmTransactions > 0 ? <React.Fragment> {t('pkn.acc.and')} <b>{t('pkn.acc.transactionsCount', { count: confirmTransactions })}</b></React.Fragment> : null} {t('pkn.acc.linkedCannotUndo')}</React.Fragment>
        : t('pkn.acc.deletePermanent')}
    </ConfirmModal>}
    {confirmJob && <ConfirmModal
      title={t('po.deleteStatement')}
      busy={deletingJobId === confirmJob.id}
      onClose={() => setConfirmJob(null)}
      onConfirm={() => { const job = confirmJob; setConfirmJob(null); deleteJob(job); }}
    >
      {t('pkn.acc.deleting')} <b>{confirmJob.file?.name || t('pkn.acc.theStatement')}</b> {t('pkn.acc.deletingSuffix')}
    </ConfirmModal>}
  </React.Fragment>;

  if (detailAccount) {
    return <React.Fragment>
      <KoDetail
        account={detailAccount}
        deletingJobId={deletingJobId}
        jobs={jobsForYear(detailAccount.id)}
        transactions={transactionsFor(detailAccount.id)}
        onBack={() => setDetailId(null)}
        onDeleteJob={onDeleteJob ? setConfirmJob : null}
        onEdit={() => setFormAccount(detailAccount)}
        year={year}
      />
      {dialogs}
    </React.Fragment>;
  }

  return <Card>
    <Sec icon="bank" title={t('po.yourAccounts')} sub={t('po.yourAccountsSub')}
      right={<Button size="sm" icon="plus" onClick={() => setFormAccount({})}>{t('sale.pay.addAccount')}</Button>} />

    <div className="ko-bar">
      <div className="ko-sum">
        <div className="ko-sum-item"><span>{t('po.totalBalance')}</span><strong>{fmt(deposits)}</strong></div>
        <div className="ko-sum-item"><span>{t('po.creditDebt')}</span><strong style={{ color: credit < 0 ? 'var(--error)' : 'var(--ink)' }}>{fmt(credit)}</strong></div>
        <div className="ko-sum-item"><span>{t('po.accounts')}</span><strong>{accounts.length}</strong></div>
      </div>
      <div className="ko-filter">
        <button className={`po-scen${filter === 'alle' ? ' on' : ''}`} onClick={() => setFilter('alle')}>{t('cust.salesAll')}</button>
        {typesPresent.map((kind) => <button key={kind} className={`po-scen${filter === kind ? ' on' : ''}`} onClick={() => setFilter(kind)}>
          <Icon name={KO_TYPES[kind].icon} size={15} />{KO_TYPES[kind].label}
        </button>)}
      </div>
    </div>

    {shown.length === 0
      ? <div className="gj-empty">{accounts.length === 0
        ? t('pkn.acc.noAccounts')
        : t('pkn.acc.noAccountsOfType')}</div>
      : <div className="gj-list" style={{ marginTop: 16 }}>
        {shown.map((account) => <KoCard
          key={account.id}
          account={account}
          statements={jobsForYear(account.id).length}
          transactions={transactionsFor(account.id).length}
          onOpen={() => setDetailId(account.id)}
          onEdit={() => setFormAccount(account)}
        />)}
      </div>}

    {dialogs}
  </Card>;
}

Object.assign(window, { PoKontoer });
